/** 微信商城表单与逐商品发布规则，供页面和批量操作复用。 */
export type ProductScene = 'GAME' | 'MALL'
export interface MallImage {
  objectKey: string
  imageUrl: string
  mimeType: string
  width: number
  height: number
  sizeBytes: number
}
export interface MallProductFields {
  prodType?: number
  prodName?: string
  prodId?: string
  prodPrice?: string | number | null
  remark?: string
  mallImageObjectKey?: string
}
export const generateMallProductId = (pkg: string, name: string) =>
  `${pkg}.${name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9.]/g, '')}`

export function validateMallProduct(product: MallProductFields): string {
  if (![10, 20].includes(Number(product.prodType))) return '请选择商品类型'
  if (!product.prodName?.trim()) return '请输入商品名称'
  if (Array.from(product.prodName.trim()).length > 10) return '商品名称不能超过10字'
  if (!product.prodId || product.prodId.length > 32) return '请手工填写不超过32位的内购ID'
  if (!/^[a-zA-Z0-9][a-zA-Z0-9._]*$/.test(product.prodId))
    return '内购ID须以字母或数字开头，仅支持字母、数字、下划线和句点'
  if (Array.from(product.remark || '').length > 50) return '备注不能超过50字'
  const price = String(product.prodPrice ?? '')
  if (!/^\d+$/.test(price) || Number(price) < 1 || Number(price) > 10000) return '默认价格须为1至10000之间的整数'
  if (!product.mallImageObjectKey) return '请上传商品图片'
  return ''
}

export type MallPublishStage = 'submit' | 'pass' | 'publish'
export interface MallPublishItem {
  id: number
  stage?: MallPublishStage
}
export interface MallPublishFailure {
  id: number
  stage: MallPublishStage
  message: string
  unknown?: boolean
}
export interface MallBatchResponse {
  code: string | number
  msg?: string
  data?: {
    successList?: { data: { id: number }; code?: string | number; msg?: string }[]
    failList?: { data: { id: number }; msg?: string }[]
  }
}
export type MallPublishApi = Record<MallPublishStage, (ids: number[]) => Promise<MallBatchResponse>>

/** 不以顶层code或请求数量推断成功；未明确成功的条目留在当前阶段。 */
export async function publishMallProducts(items: MallPublishItem[], api: MallPublishApi) {
  const stages: MallPublishStage[] = ['submit', 'pass', 'publish']
  const pending = new Map(items.map((item) => [item.id, item.stage || 'submit']))
  const failures: MallPublishFailure[] = []
  let successIds: number[] = []
  for (const stage of stages) {
    const ids = [...pending].filter(([, value]) => value === stage).map(([id]) => id)
    if (!ids.length) continue
    try {
      const response = await api[stage](ids)
      // 项目请求层将网络异常转换为6003，仍属结果未知，不能直接重发。
      if (String(response?.code) === '6003') throw new Error('请求结果未知')
      const data = response.data
      const successful = new Set(
        (data?.successList || []).map((item) => item.data?.id).filter((id) => ids.includes(id)),
      )
      const failed = new Map(
        (data?.failList || []).map((item) => [item.data?.id, item.msg === '成功' ? undefined : item.msg]),
      )
      for (const id of ids) {
        pending.delete(id)
        if (!successful.has(id) || failed.has(id)) {
          failures.push({
            id,
            stage,
            unknown: stage === 'publish' && !failed.has(id) ? true : undefined,
            message:
              failed.get(id) ||
              (String(response.code) !== '0' ? response.msg : '') ||
              '未收到该商品的成功结果，请核对状态后重试',
          })
        } else if (stage !== 'publish') {
          pending.set(id, stages[stages.indexOf(stage) + 1])
        }
      }
      if (stage === 'publish') {
        successIds = ids.filter((id) => successful.has(id) && !failed.has(id))
      }
    } catch (error) {
      for (const id of ids) {
        pending.delete(id)
        failures.push({ id, stage, message: '请求结果未知，请返回列表核对商品当前状态', unknown: true })
      }
    }
  }
  return { successIds, failures }
}

/** 上传前检查实际文件头及浏览器解码结果；不改尺寸、不重编码原图。 */
export async function validateMallImage(file: File): Promise<string> {
  if (!file.size || file.size >= 204800) return '商品图片不能为空且必须小于200KB'
  const header = new Uint8Array(await file.slice(0, 8).arrayBuffer())
  const png = [137, 80, 78, 71, 13, 10, 26, 10].every((value, index) => header[index] === value)
  const jpeg = header[0] === 255 && header[1] === 216 && header[2] === 255
  if (!png && !jpeg) return '商品图片仅支持JPG或PNG'
  try {
    const bitmap = await createImageBitmap(file)
    const correct = bitmap.width === 200 && bitmap.height === 200
    bitmap.close()
    return correct ? '' : '商品图片尺寸必须为200×200像素'
  } catch (error) {
    return '商品图片无法解码，请选择有效的JPG或PNG'
  }
}

/** 键入/粘贴按整次候选值检查，非法输入不会被删字符后改成另一个金额。 */
export function isMallPriceInsertionAllowed(value: string, start: number, end: number, inserted: string): boolean {
  const next = value.slice(0, start) + inserted + value.slice(end)
  return /^\d*$/.test(next) && next.length <= 5 && Number(next || '0') <= 10000
}
